import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../../lib/i18n";
import { Reveal, Kicker, SectionTitle } from "./Reveal";
import { Wave } from "./Wave";

export const Origin = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const clip = useTransform(scrollYProgress, [0.05, 0.4], ["inset(18% 18% 18% 18% round 200px)", "inset(0% 0% 0% 0% round 28px)"]);

  return (
    <section id="origin" ref={ref} className="relative overflow-hidden bg-[#073B4C] py-32 text-[#FBF7EF] md:py-44" data-testid="origin-section">
      <img src="/img/underwater.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#073B4C]/50 via-[#073B4C]/80 to-[#073B4C]/95" />
      <Wave fill="#FBF7EF" top />
      <Wave fill="#FBF7EF" />
      <div className="relative z-[2] mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal><Kicker light>{t.origin.kicker}</Kicker></Reveal>
          <Reveal delay={0.05}><SectionTitle light className="mt-6">{t.origin.title}</SectionTitle></Reveal>
          <Reveal delay={0.1}><p className="mt-8 max-w-xl leading-relaxed text-[#FBF7EF]/80">{t.origin.body}</p></Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 rounded-2xl border border-[#7ED9D2]/30 p-6" data-testid="origin-ingredient">
              <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-[#7ED9D2]">{t.origin.ingredientLabel}</p>
              <p className="mt-2 font-display text-2xl leading-snug md:text-3xl">{t.origin.ingredient}</p>
            </div>
          </Reveal>

          <ol className="mt-12 space-y-0">
            {t.origin.steps.map((s, i) => (
              <Reveal key={s.n} delay={0.1 + i * 0.08} as="li" className="flex gap-6 border-t border-[#FBF7EF]/15 py-5">
                <span className="font-mono-x text-sm text-[#7ED9D2]">0{i + 1}</span>
                <div>
                  <p className="font-display text-2xl">{s.n}</p>
                  <p className="text-sm text-[#FBF7EF]/75">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>

        <div className="lg:col-span-6">
          <div className="relative">
            <motion.div style={{ clipPath: clip }} className="relative aspect-[4/5] overflow-hidden rounded-[28px]">
              <motion.img style={{ y }} src="/img/waves.jpg" alt="Vagues sur la côte" className="absolute inset-0 h-[116%] w-full -translate-y-[8%] object-cover" />
            </motion.div>
            <Reveal delay={0.2} className="relative -mt-20 ml-auto w-[88%] rounded-2xl bg-[#FBF7EF] p-6 text-[#073B4C] shadow-2xl md:w-[75%]">
              <p className="font-display text-2xl italic">{t.origin.balanceTitle}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#3F5F68]" data-testid="origin-balance">{t.origin.balance}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
